import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetDomain, useHasPermission } from "../../common/Hooks";
import AuditTrail from "../../services/auditTrail";
import Toolbar from "../controls/Toolbar";
import List from "../controls/List";
import { useHistory } from "react-router-dom";
import Utils from "../../utils/utils";

const SessionList = ({ onClick = () => { } }) => {
    const history = useHistory();
    const [translate] = useTranslation();
    const [sessionParams, setSessionParams] = useState({});
    const [sessions, setSessions] = useState([]);
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);

    const [domain] = useGetDomain();

    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "auditTrail"
    });

    const getSessions = useCallback(async (params) => {
        const response = await AuditTrail.getAllSessionsInDomain(params.domainId, params.firstCursor, params.lastCursor, params.pageIncrement < 0);
        if (!response.error) {
            setSessions(response.data);
            setCurrentPage(page => {
                const result = page + params.pageIncrement;
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });

            const focusId = response.data[0]?.sessionId;
            if (focusId) {
                history.push(`/audit-trail/${focusId}`);
            }
            else {
                history.push("/audit-trail");
            }
        }
    }, [history]);

    useEffect(() => {
        setSessionParams({
            domainId: domain?._id,
            pageIncrement: 0
        });
    }, [domain]);

    useEffect(() => {
        if (sessionParams.domainId) {
            getSessions(sessionParams);
        }
    }, [sessionParams, getSessions]);

    const previous = useCallback(() => {
        setSessionParams({
            domainId: domain?._id,
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [domain, cursors.firstCursor, cursors.lastCursor]);

    const next = useCallback(() => {
        setSessionParams({
            domainId: domain?._id,
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [domain, cursors.firstCursor, cursors.lastCursor]);

    const handleClick = useCallback((sessionId) => {
        history.push("/audit-trail/" + sessionId);
        onClick();
    }, [history, onClick]);

    const renderToolbar = useMemo(() => {
        return (
            <Toolbar
                buttons={[
                    {
                        name: "ChevronLeft",
                        title: translate("OTM_PREVIOUS"),
                        onClick: previous,
                        disabled: currentPage === 1
                    },
                    {
                        name: "ChevronRight",
                        title: translate("OTM_NEXT"),
                        onClick: next,
                        disabled: !hasNextPage
                    }
                ]}
                heading={translate("OTM_SESSIONS")}
            />
        );
    }, [previous, currentPage, next, hasNextPage, translate]);

    const renderList = useMemo(() => {
        if (permissions.read) {
            return (
                <div className="card">
                    <div className="card-header border-bottom">
                        {renderToolbar}
                    </div>
                    <div className="card-body">
                        <List onClick={handleClick} attr="sessionId" elementId="sessionId" data={sessions} url="/audit-trail/" type="auditTrail" />
                    </div>
                </div>
            );
        }
    }, [handleClick, permissions.read, sessions, renderToolbar])

    return (
        <>
            {renderList}
        </>
    );
};

export default SessionList;